import { Injectable, signal, untracked, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseProjectService, StorageService, OfflineModeService, ConnectionService } from '@luzula/luzula-lib';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from './auth.service';
import { RoleDefinition } from './role-model';
import { cloneDeep } from 'lodash';
import { MapDefinition, Regions } from '@luzula/luzula-lib';
import { DatabaseConfigService } from './database-config.service';

export interface ProjectMember {
	id: string;
	username: string;
	firstName?: string;
	lastName?: string;
}

/**
 * Berechtigungen für Punkte und Bereiche im Projekt.
 * Anlegen und Importieren implizieren Ändern.
 */
export interface ProjectPermissions {
	anlegen: boolean;         // Punkte anlegen
	loeschen: boolean;        // Punkte löschen
	importieren: boolean;     // Punkte importieren
	aendern: boolean;         // Punkte ändern
	zuordnen: boolean;        // Punkte dem Projekt zuordnen
	bereicheAnlegen: boolean; // Bereiche anlegen (Rechteck, Polygon, GeoJSON-Import)
	bereicheLoeschen: boolean; // Bereiche löschen
}

/**
 * Metadaten für Projekt-Dateianhänge.
 * Die eigentlichen Binärdaten werden als PouchDB _attachments gespeichert.
 */
export interface ProjectAttachmentMeta {
	name: string;           // Original-Dateiname
	contentType: string;    // MIME-Type (image/jpeg, application/pdf, etc.)
	size: number;           // Dateigröße in Bytes
	uploadedAt: string;     // ISO-Datum
}

export interface Project {
	name: string;
	description: string;
	mapDefinition?: MapDefinition;
	regions?: Regions;

	owner?: ProjectMember;
	bearbeiter?: ProjectMember[];
	kontrolleure?: ProjectMember[];

	permissions?: ProjectPermissions;

	lastOpenedSection?: string;

	// Liste der hinzugefügten Geodatendateien (Namen als Referenz)
	geodataFiles?: string[];

	// Metadaten für Dateianhänge (Binärdaten in _attachments)
	attachmentsMeta?: ProjectAttachmentMeta[];
}

export interface ProjectDocument extends Project {
	_id: string;
	_rev?: string;
}

@Injectable({
	providedIn: 'root'
})
export class ProjectService extends BaseProjectService<ProjectDocument> {
	private dbConfig = inject(DatabaseConfigService);

	// ========== Abstract Service Getter Implementierungen ==========

	protected override get storage() { return this._storage; }
	protected override get oms() { return this._oms; }
	protected override get auth() { return this._auth; }
	protected override get msg() { return this._msg; }
	protected override get connectionMonitor(): Observable<boolean> { return this.cs.monitor(); }
	protected override get authChanged(): Observable<void> { return this._auth.authChanged; }

	// ========== BasePouchDbService Implementierungen ==========

	get remoteDbName(): string {
		return this.dbConfig.projekteDbName;
	}

	protected get userOfflineModeSignal() {
		return this._oms.userOfflineMode;
	}

	protected get networkOnlineSignal() {
		// ProjectService nutzt ConnectionService, nicht OfflineModeService.networkOnline
		// Return constant signal - Netzwerkänderungen werden über connectionMonitor behandelt
		return signal<boolean>(true);
	}

	// ========== Hooks ==========

	protected override async initDesignDocs(db: PouchDB.Database): Promise<void> {
		const views = {
			_id: '_design/app_views',
			views: {
				by_id: {
					map: `function(doc) { emit(doc._id, { name: doc.name, description: doc.description, owner: doc.owner, bearbeiter: doc.bearbeiter, kontrolleure: doc.kontrolleure });}`
				},
				by_member: {
					map: `function(doc) {
						var value = { _id: doc._id, name: doc.name, description: doc.description, owner: doc.owner, bearbeiter: doc.bearbeiter, kontrolleure: doc.kontrolleure };
						if (doc.owner && doc.owner.id) {
							emit(doc.owner.id, value);
						}
						if (doc.bearbeiter && Array.isArray(doc.bearbeiter)) {
							doc.bearbeiter.forEach(function(k) {
								if (k.id) emit(k.id, value);
							});
						}
						if (doc.kontrolleure && Array.isArray(doc.kontrolleure)) {
							doc.kontrolleure.forEach(function(k) {
								if (k.id) emit(k.id, value);
							});
						}
					}`
				}
			}
		}
		try {
			const existing = await db.get('_design/app_views');
			const existingWithoutRev = cloneDeep(existing);
			delete (existingWithoutRev as any)._rev;
			if (JSON.stringify(existingWithoutRev) !== JSON.stringify(views)) {
				await db.put({
					...views,
					_rev: existing._rev
				});
			}
		} catch (err: any) {
			if (err.status === 404) {
				await db.put(views);
			}
		}
	}

	protected override onProjectSaved(doc: any): void {
		// Permissions könnten sich geändert haben → authChanged triggern
		this._auth.authChanged.emit();
	}

	// ========== Constructor ==========

	constructor(
		private _msg: ToastrService,
		private cs: ConnectionService,
		private _storage: StorageService,
		private _oms: OfflineModeService,
		private _auth: AuthService
	) {
		super();
		this.initializeBaseProjectService();
	}

	// ========== App-spezifische abstrakte Methoden ==========

	/**
	 * Aktualisiert die Projektliste basierend auf dem aktuellen Modus.
	 * Manager sehen alle Projekte, andere Benutzer nur Projekte,
	 * für die sie Eigentümer, Kartierer oder Kontrolleur sind.
	 */
	protected async refreshProjectList(): Promise<void> {
		if (this._oms.userOfflineMode() && this.localDb) {
			// Im Offline-Modus: Nur lokale Projekte anzeigen
			const docs = await this.localDb.allDocs({ include_docs: true });
			const projects = docs.rows
				.filter(row => !row.id.startsWith('_design/'))
				.map(row => ({
					_id: row.id,
					name: (row.doc as any)?.name ?? row.id,
					description: (row.doc as any)?.description,
					owner: (row.doc as any)?.owner,
					bearbeiter: (row.doc as any)?.bearbeiter,
					kontrolleure: (row.doc as any)?.kontrolleure
				} as ProjectDocument));
			this.projectList.set(projects);
		} else if (this.remoteDb) {
			if (this._auth.canIbe('manager') || this._auth.canIbe('entwickler')) {
				// Manager und Entwickler sehen alle Projekte
				const docs = await this.remoteDb.query<Project>('app_views/by_id', { include_docs: false });
				this.projectList.set(docs.rows.map((row: any) => ({
					_id: row.id,
					name: row.value.name,
					description: row.value.description,
					owner: row.value.owner,
					bearbeiter: row.value.bearbeiter,
					kontrolleure: row.value.kontrolleure
				})));
			} else {
				// Andere Benutzer sehen nur eigene Projekte
				const userId = this._auth.currentUserId;
				if (!userId) {
					this.projectList.set([]);
					return;
				}

				const docs = await this.remoteDb.query<Project>('app_views/by_member', {
					key: userId,
					include_docs: false
				});

				// Duplikate entfernen
				const uniqueProjects = new Map<string, ProjectDocument>();
				docs.rows.forEach((row: any) => {
					if (!uniqueProjects.has(row.value._id)) {
						uniqueProjects.set(row.value._id, {
							_id: row.value._id,
							name: row.value.name,
							description: row.value.description,
							owner: row.value.owner,
							bearbeiter: row.value.bearbeiter,
							kontrolleure: row.value.kontrolleure
						} as ProjectDocument);
					}
				});
				this.projectList.set([...uniqueProjects.values()]);
			}
		}
	}

	/**
	 * Generiert eine Projekt-ID nach dem Schema YYYYMMDD_Projektname_GUID
	 */
	protected override generateProjectId(projectName: string): string {
		const now = new Date();
		const year = now.getFullYear();
		const month = String(now.getMonth() + 1).padStart(2, '0');
		const day = String(now.getDate()).padStart(2, '0');
		const dateStr = `${year}${month}${day}`;

		const sanitizedName = projectName
			.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
			.replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
			.replace(/ß/g, 'ss')
			.replace(/[^a-zA-Z0-9]/g, '_')
			.replace(/_+/g, '_')
			.replace(/^_|_$/g, '');

		const guid = crypto.randomUUID().split('-')[0];

		return `${dateStr}_${sanitizedName}_${guid}`;
	}

	// ========== Berechtigungsprüfungen ==========

	/**
	 * Prüft eine Rollen-Permission direkt gegen die RoleDefinition.
	 */
	private checkRolePermission(role: string, access: string, resource: string): boolean {
		const matrix = RoleDefinition.get(role);
		if (!matrix) return false;
		const res = matrix.get(resource);
		return res?.has(access) ?? false;
	}

	/**
	 * Projekt-aware Berechtigungsprüfung.
	 */
	canI(access: string, resource: string): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());

		if (!projectDoc) {
			if (resource === 'points') {
				return this.checkRolePermission('gast', access, resource);
			}
			return this._auth.canI(access, resource);
		}

		const projectPermits = () => {
			if (resource === 'points') {
				switch (access) {
					case 'add': if (!projectDoc.permissions?.anlegen && !projectDoc.permissions?.importieren) return false; break;
					case 'write': if (!projectDoc.permissions?.aendern) return false; break;
				}
			}
			return true;
		}

		if ((this._auth.amI('kartierer') || this._auth.amI('entwickler')) && this.isBearbeiter()) {
			return projectPermits() && this.checkRolePermission('kartierer', access, resource);
		} else if (this._auth.amI('kontrolleur') && this.isKontrolleur()) {
			return projectPermits() && this.checkRolePermission('kontrolleur', access, resource);
		}

		const hasNoBearbeiter = !projectDoc.bearbeiter || projectDoc.bearbeiter.length === 0;
		if (this.isOwner() && hasNoBearbeiter) {
			return projectPermits() && this._auth.canI(access, resource);
		}

		if (this._auth.amI('gast') || this._auth.amI('datennutzer')) {
			return this.checkRolePermission('gast', access, resource);
		}

		return false;
	}

	canIadd(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		if (!(projectDoc.permissions?.anlegen ?? false)) return false;

		const hasNoBearbeiter = !projectDoc.bearbeiter || projectDoc.bearbeiter.length === 0;
		if (this.isOwner() && hasNoBearbeiter) {
			return this._auth.canI('add', 'points');
		}

		if (!this.isBearbeiter()) return false;
		if (!this._auth.amI('kartierer') && !this._auth.amI('entwickler')) return false;
		return this.checkRolePermission('kartierer', 'add', 'points');
	}

	canIremove(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		if (!(projectDoc.permissions?.loeschen ?? false)) return false;

		const hasNoBearbeiter = !projectDoc.bearbeiter || projectDoc.bearbeiter.length === 0;
		if (this.isOwner() && hasNoBearbeiter) {
			return true;
		}

		if (!this.isBearbeiter()) return false;
		if (!this._auth.amI('kartierer') && !this._auth.amI('entwickler')) return false;
		return this.checkRolePermission('kartierer', 'write', 'points');
	}

	canIimport(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		if (!(projectDoc.permissions?.importieren ?? false)) return false;

		if (this._auth.amI('manager') || this._auth.amI('entwickler')) {
			return true;
		}

		const hasNoBearbeiter = !projectDoc.bearbeiter || projectDoc.bearbeiter.length === 0;
		if (this.isOwner() && hasNoBearbeiter) {
			return false;
		}

		if (!this.isBearbeiter()) return false;
		if (!this._auth.amI('kartierer')) return false;
		return this.checkRolePermission('kartierer', 'add', 'points');
	}

	amI(role: string): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());

		if (!projectDoc) {
			return this._auth.amI(role);
		}

		if (role === 'kartierer') {
			return this._auth.amI('kartierer') && this.isBearbeiter();
		} else if (role === 'kontrolleur') {
			return this._auth.amI('kontrolleur') && this.isKontrolleur();
		}

		return this._auth.amI(role);
	}

	override canIbe(role: string): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());

		if (!projectDoc) {
			return this._auth.canIbe(role);
		}

		if (role === 'kartierer') {
			return this._auth.canIbe('kartierer') && this.isBearbeiter();
		} else if (role === 'kontrolleur') {
			return this._auth.canIbe('kontrolleur') && this.isKontrolleur();
		}

		return this._auth.canIbe(role);
	}

	isBearbeiter(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		const userId = this._auth.currentUserId;
		return projectDoc.bearbeiter?.some(b => b.id === userId) ?? false;
	}

	isKontrolleur(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		const userId = this._auth.currentUserId;
		return projectDoc.kontrolleure?.some(k => k.id === userId) ?? false;
	}

	override isOwner(): boolean {
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc) return false;
		return projectDoc.owner?.id === this._auth.currentUserId;
	}

	override amIMember(): boolean {
		return this.isOwner() || this.isBearbeiter() || this.isKontrolleur();
	}

	canModifyProject(): boolean {
		// Wenn der User eine echte Rolle hat (roles-Set gefüllt), ist er authentifiziert —
		// unabhängig vom BehaviorSubject-State, der nach Token-Refresh-Problemen
		// oder Auto-Logout inkonsistent sein kann
		if (this._auth.canIbe('manager') || this._auth.canIbe('kartierer') ||
			this._auth.canIbe('kontrolleur') || this._auth.canIbe('entwickler')) return true;
		if (!this._auth.isAuthenticated.getValue()) return false;
		if (this._auth.amI('gast')) return false;
		return true;
	}

	override canEditRegions(): boolean {
		if (!this.canModifyProject()) return false;
		return this.isOwner() || this._auth.canIbe('manager');
	}

	canDeleteProject(): boolean {
		if (!this.canModifyProject()) return false;
		return this.isOwner() || this._auth.canIbe('manager');
	}

	override canCreateRegions(): boolean {
		if (!this.canModifyProject()) return false;
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc?.permissions?.bereicheAnlegen) return false;
		return this.amIMember() || this._auth.canIbe('manager');
	}

	override canDeleteRegions(): boolean {
		if (!this.canModifyProject()) return false;
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc?.permissions?.bereicheLoeschen) return false;
		return this.amIMember() || this._auth.canIbe('manager');
	}

	override canAssignToProject(): boolean {
		if (!this.canModifyProject()) return false;
		const projectDoc = untracked(() => this.currentProjectDoc());
		if (!projectDoc?.permissions?.zuordnen) return false;
		return this.amIMember() || this._auth.canIbe('manager');
	}
}
